import { OrganisationInvitationControllerApiFactory } from '@api-client/api'
import { InviteModal } from '@components/Modals/InviteModal'
import { useWithAmpli } from '@contexts/AmpliContextProvider'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { groupBy } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
	isOpen: boolean
	onClose: () => void
	onInvitationsSent?: () => void
}

interface ErrorResponse {
	response: {
		status: number
		data: readonly { email: string; status: 'DUPLICATED' | 'CONFLICTING' }[]
	}
}

const InviteSuppliersModal = (props: Props) => {
	const { isOpen, onClose, onInvitationsSent } = props
	const [invitationErrors, setInvitationErrors] = useState<readonly string[]>(
		[]
	)
	const { enqueueSnackbar } = useSnackbar()
	const { t } = useTranslation()
	const withAmpli = useWithAmpli()
	const invitationControllerApi = useAsyncApi(
		OrganisationInvitationControllerApiFactory
	)

	if (!isOpen && invitationErrors.length > 0) {
		setInvitationErrors([])
	}

	const sendInvitations = useCallback(
		async (emails: readonly string[]) => {
			if (emails.length === 0) {
				enqueueSnackbar(t('toast.error.general'), {
					variant: 'error',
				})
				return
			}

			try {
				const emailsToNotifyPerPrimaryEmail = Object.fromEntries(
					emails.map(email => [email, []])
				)
				await invitationControllerApi(api =>
					api.createInvitation({ emailsToNotifyPerPrimaryEmail })
				)
				onClose()
				withAmpli(ampli =>
					ampli.clickEvent({
						buttonText: 'Click - Send invitation',
						clickPageUrl: window.location.href,
					})
				)
				enqueueSnackbar(t('toast.success.suppliersInvited'), {
					variant: 'success',
				})
				onInvitationsSent?.()
			} catch (err) {
				const error = err as ErrorResponse
				if (error.response.status === 409) {
					const emailsByStatus = groupBy(
						error.response?.data ?? [],
						item => item.status
					)
					const invitationErrors = [
						emailsByStatus.CONFLICTING
							? t('validationError.supplierConflicting', {
									email: emailsByStatus.CONFLICTING.map(
										item => item.email
									).join(', '),
								})
							: null,
						emailsByStatus.DUPLICATED
							? t('validationError.supplierDuplicated', {
									email: emailsByStatus.DUPLICATED.map(item => item.email).join(
										', '
									),
								})
							: null,
					].filter((error): error is string => error !== null)
					setInvitationErrors(invitationErrors)
				}
			}
		},
		[
			enqueueSnackbar,
			invitationControllerApi,
			onClose,
			onInvitationsSent,
			t,
			withAmpli,
		]
	)
	return (
		<InviteModal
			open={isOpen}
			onClose={onClose}
			onActionClick={sendInvitations}
			headerText={t('inviteSupplier')}
			errors={invitationErrors}
		/>
	)
}

export { InviteSuppliersModal }
